import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { preconnectRelays } from './hooks/useNostrQuery';
import { preloadWallet } from './hooks/useTipWallet';
import { initBCHPrice } from './hooks/useBCHPrice';
import { captureReferral } from './lib/referral';
import { initNip05Verification } from './lib/nip05verify';
import { registerServiceWorker } from './lib/pushNotifications';

// ── Pre-React crash recovery ─────────────────────────────────────
// If the app crashes before React mounts (corrupt localStorage,
// module load failure, etc.), show a minimal recovery UI so the
// user isn't stuck on a blank screen forever.
function showCrashRecovery(error: unknown) {
  const msg = error instanceof Error ? error.message : String(error);
  document.body.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui,sans-serif;padding:1.5rem;background:#0f0f0f;color:#e5e5e5">
      <div style="max-width:420px;width:100%;text-align:center">
        <div style="font-size:2rem;margin-bottom:0.5rem;font-weight:700">BCHNostr</div>
        <div style="width:3rem;height:3px;margin:0 auto 1.5rem;background:linear-gradient(90deg,#10b981,#0d9488);border-radius:2px"></div>
        <h1 style="font-size:1.15rem;font-weight:700;margin-bottom:0.5rem">The app couldn't start</h1>
        <p style="color:#a3a3a3;line-height:1.5;margin-bottom:1.5rem;font-size:0.85rem">
          This is usually caused by corrupted cached data. Clearing the cache will fix it.
          Your wallet keys and login are preserved.
        </p>
        <div style="display:flex;flex-direction:column;gap:0.75rem">
          <button onclick="try{var p=['bchnostr:current-user','bchnostr:login-method','bchnostr:business-identities','bchnostr:owner-session'];var s={};for(var i=0;i<localStorage.length;i++){var k=localStorage.key(i);if(k&&(p.indexOf(k)>=0||k.indexOf('tip-wallet-wif')>=0||k.indexOf('nsec')>=0)){s[k]=localStorage.getItem(k)}}localStorage.clear();sessionStorage.clear();for(var k in s)localStorage.setItem(k,s[k])}catch(e){}location.href='/'"
            style="padding:0.75rem;border-radius:0.75rem;border:none;background:linear-gradient(135deg,#10b981,#0d9488);color:white;font-weight:700;cursor:pointer;font-size:0.9rem">
            Clear Cache &amp; Reload
          </button>
          <button onclick="location.reload()"
            style="padding:0.6rem;border-radius:0.75rem;border:1px solid #333;background:#1a1a1a;color:#e5e5e5;font-weight:600;cursor:pointer;font-size:0.85rem">
            Just Reload
          </button>
          <button onclick="try{localStorage.clear();sessionStorage.clear()}catch(e){}location.href='/'"
            style="padding:0.5rem;background:none;border:none;color:#737373;cursor:pointer;font-size:0.75rem">
            Full Reset (clears everything including login)
          </button>
        </div>
        <details style="margin-top:1.5rem;text-align:left">
          <summary style="cursor:pointer;color:#737373;font-size:0.7rem">Error details</summary>
          <pre style="font-size:0.65rem;padding:0.5rem;background:#1a1a1a;color:#f87171;border-radius:0.5rem;overflow:auto;max-height:100px;margin-top:0.5rem;border:1px solid #262626">${msg.replace(/</g, '&lt;')}</pre>
        </details>
      </div>
    </div>`;
}

// Catch unhandled errors and promise rejections globally
window.addEventListener('error', (e) => {
  // Only show crash recovery if the root element is empty (app never rendered)
  const root = document.getElementById('root');
  if (root && root.children.length === 0) {
    showCrashRecovery(e.error || e.message);
  }
});
window.addEventListener('unhandledrejection', (e) => {
  const root = document.getElementById('root');
  if (root && root.children.length === 0) {
    showCrashRecovery(e.reason);
  }
});

// Pre-connect to relays IMMEDIATELY — before React even mounts
// This means WebSockets are already OPEN by the time the first query fires
try { preconnectRelays(); } catch {}

// Preload mainnet-js in background so tip wallet is instant when needed
setTimeout(() => { try { preloadWallet(); } catch {} }, 2000);

// Start fetching BCH price immediately so tip dropdown has it cached
try { initBCHPrice(); } catch {}

// Load the BCHNostr NIP-05 directory and keep it refreshed. One fetch
// answers every verified-badge lookup in the app, so doing it up front
// means badges are correct on first paint rather than popping in late.
try { initNip05Verification(); } catch {}

// Capture ?ref= referral code from URL before React mounts
try { captureReferral(); } catch {}

// Register the service worker so the app is installable and works offline.
// Runs after window load so it never competes with first paint.
try { registerServiceWorker(); } catch {}

// ── Proactive localStorage sanitization ──────────────────────────
// Validate and repair potentially corrupted data BEFORE React mounts.
// This prevents the blank screen / crash loop for returning users.
try {
  // 1. Clear stale experimental data
  ['relayScores','mlSpamModel','goodRelays','badRelays','tipScores',
   'bchActivityScores','trustedPubkeys','followedPubkeys','activeHashtag',
   'bchnostr:bot-cache','bchnostr:relay-cache']
  .forEach(k => localStorage.removeItem(k));

  // 2. Validate all JSON entries — remove any that are corrupted
  const keysToCheck = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) keysToCheck.push(key);
  }
  for (const key of keysToCheck) {
    try {
      const val = localStorage.getItem(key);
      if (val && val.startsWith('{') || val && val.startsWith('[')) {
        JSON.parse(val); // Just test — if it throws, the data is corrupt
      }
    } catch {
      // Corrupt JSON — remove it, but preserve wallet WIF and login keys
      if (!key.includes('tip-wallet-wif') && !key.includes('nsec') &&
          key !== 'bchnostr:current-user' && key !== 'bchnostr:login-method') {
        console.warn('[Sanitize] Removing corrupted localStorage key:', key);
        localStorage.removeItem(key);
      }
    }
  }

  // 3. Prune oversized profile store (can grow to megabytes and slow parse)
  const profileStore = localStorage.getItem('bchnostr:profiles');
  if (profileStore && profileStore.length > 500_000) { // > 500KB
    console.warn('[Sanitize] Profile store too large (' + (profileStore.length / 1024).toFixed(0) + 'KB), clearing');
    localStorage.removeItem('bchnostr:profiles');
  }

  // 4. Prune oversized user pubkeys list
  const pubkeyStore = localStorage.getItem('bchnostr:user-pubkeys');
  if (pubkeyStore && pubkeyStore.length > 300_000) { // > 300KB
    console.warn('[Sanitize] User pubkeys store too large, clearing');
    localStorage.removeItem('bchnostr:user-pubkeys');
  }
} catch(e) {
  // If sanitization itself fails, try nuclear option on non-essential data
  try {
    localStorage.removeItem('bchnostr:profiles');
    localStorage.removeItem('bchnostr:user-pubkeys');
  } catch {}
}

try {
  ReactDOM.createRoot(document.getElementById('root')!).render(<App />);
} catch (e) {
  showCrashRecovery(e);
}

